import { Injectable } from '@angular/core';
import { AppInjector } from '../../app-injector';
import { IntegrationItemServiceInf, StatusString } from '../interfaces/integrations';
import { Account, RouterService } from '@mitel/cloudlink-sdk';
import { AdminService } from './admin.service';
import { MenuItem } from '../interfaces/menu-item'; 
import { IntegSharedCCService } from './integ-shared-cc.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogs } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialogs.enum';
import { environment } from '../../../environments/environment';
import { RBACAuthGuardService } from '../guards/rbac-auth-guard.service';
import { ActionType, CategoryId } from '../utils/rbac.util';
import { UserRoles } from '../constants/constants';
import { TranslateService } from '@ngx-translate/core';
import { OrdersService } from './orders.service';
import { XLaunchService } from './x-launch.service';
import { StatusType } from '../constants/statusType.enum';
import { Util } from '../utils/Util';

@Injectable({
	providedIn: 'root'
})

// this service will hide whatever implementation we eventually have for gettinng the list of integrations
// types. Right now it is static
export class IntegWorkgroupsService implements IntegrationItemServiceInf {
	private adminService: AdminService = undefined;
	private sharedCCService: IntegSharedCCService = undefined;
	public dialog: MatDialog;
	private rbacAuthGuardService: RBACAuthGuardService;
	private routerService: RouterService;
	private translateService: TranslateService = undefined;
	private ordersService: OrdersService;
	private xlaunchService: XLaunchService;

	constructor(  ) 
	{
	}

	initServices()
	{
		if ( !this.adminService )
			this.adminService = AppInjector.injector.get(AdminService);

		if ( !this.sharedCCService )
			this.sharedCCService = AppInjector.injector.get(IntegSharedCCService);

		if ( !this.dialog )
			this.dialog = AppInjector.injector.get(MatDialog);

		if ( !this.rbacAuthGuardService )
			this.rbacAuthGuardService = AppInjector.injector.get(RBACAuthGuardService);

		if ( !this.routerService )
			this.routerService = AppInjector.injector.get(RouterService);

		if ( !this.translateService )
			this.translateService = AppInjector.injector.get(TranslateService);		

		if ( !this.ordersService )
			this.ordersService = AppInjector.injector.get(OrdersService);	

		if ( !this.xlaunchService )
			this.xlaunchService = AppInjector.injector.get(XLaunchService);
	}
	
	canBeSelected(account: Account): Promise<boolean>
	{
		this.initServices();
		if ( this.xlaunchService.isXLaunchedFromBoss() )
			return Promise.resolve(false);
		return Promise.resolve(true);
	}

	getConfigurationDialog() : any
	{
		this.initServices();
		return this.sharedCCService.getConfigurationDialog();
	}

	isSetupComplete(account: Account) : Promise<boolean>
	{
		this.initServices();
		// current understanding on whether setup is complete
		// check for the cx tag to determine if setup is complete
		// Account tag = cx - value = cc-next-beta and the state- i.e. the tag indicates the setup is run and the value is the edition.
		// if the state tag is set to FINISHED, setup is complete, otherwise it is not
		return this.sharedCCService.isSetupComplete( account, 'cc-next-beta' );
	}

	canSetupProceed(account: Account): Promise<boolean>
	{
		this.initServices();
		// for all types we will check for the existance of a Mitel One subscription
		// if it does not exist, display an error
		return this.ordersService.doesCustomerAccountHaveExistingProductSubs(environment.subscriptionIds.MitelOne).then(existingSubs => {
			if ( existingSubs === true )
			{
				console.log("THere are existing Mitel One subscriptions for this account", account);

				// for a non-xlaunch environment, we need to make sure there is a GW integration,
				// MiVoice Office 400, and what else?
				return this.adminService.getAccountTags(account.accountId).then( tags => {
					if ( Util.doesAccountHaveMiVoice400Onboarded(tags) == true )
					{
						return true;
					}

					// show the dialog that says what the requirement is to have a CX integration
					const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.MV400_CX_SETUP_FAILURE } });
					return dialogRef.afterClosed().toPromise().then(result => {
						return false;
					});
				})
			}
			else
			{
				// there is no Mitel One subscription, so there can be no CX integration
				// show a dialog. 
				const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.MV400_CX_SETUP_FAILURE } });
				return dialogRef.afterClosed().toPromise().then(result => {
					return false;
				});
			}
		});
	}

	handleIntegrationStateChange(account: Account, state: boolean, productsPriorChange?: string[]): Promise<any>
	{
		this.initServices();
		return this.sharedCCService.handleIntegrationStateChange( 
				account, 
				state,
				"Workgroups");
	}

	postCompleteIntegration(account: Account): Promise<boolean>{
		return Promise.resolve(true);
	}

	getStatusString(account: Account): Promise<StatusString>
	{
		this.initServices();
		let status: StatusString = {
			status: 'integrations.workgroups.no-subscription',
			type: StatusType.caution
		}
		// check to see if there is a mitel One sub for the account
		// If no, then show a caution that a subscription is needed

		return this.ordersService.doesCustomerAccountHaveExistingProductSubsAndTier(environment.subscriptionIds.MitelOne, ["MitelOne_Elite"]).then(existingSubs => {
			if ( existingSubs === true )
			{
				return undefined;
			}
			else
			{
				return status;
			}
		})
		.catch( err => {
			console.log("Could not retrieve subscriptions for the account, assume none", err);
			return status;
		})
	}

	getConfigButtonLabel(account: Account): Promise<string>
	{
		return undefined;
	}

	isButtonDisabled(account: Account): Promise<boolean>
	{
		return Promise.resolve(false);
	}

	canDeleteIntegration(account: Account): Promise<boolean>
	{
		this.initServices();
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.REMOVE_WORKGROUPS_CONFIRMATION } });
		return dialogRef.afterClosed().toPromise().then(result => {
			return result;
		});
	}

	getLeftNavMenus( account: Account ): Promise<MenuItem[]>
	{
		this.initServices();
		return this.isSetupComplete( account ).then(complete => {
			if ( complete == false ) // setup has to be complete before we can display the menus
				return [];

			const dashboardItem = {
				name: 'ccDashboard',
				translationKey: 'About',
				id: 'ccdashboard',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/ccdashboard`						
			};
			
			const skillsGroupItem = {
				name: 'ccSkillsGroup',
				translationKey: 'SkillGroups',
				id: 'ccskillgroups',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/ccskills`						
			};

			const routingItem = {
				name: 'ccRouting',
				translationKey: 'Routing',
				id: 'ccroutings',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/ccrouting`						
			};

			const codesItem = {
				name: 'ccCodes',
				translationKey: 'Codes',
				id: 'ccadvanced',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/cccodes`						
			};

			const templatesItem = {
				name: 'ccTemplates',
				translationKey: 'Templates',
				id: 'cctemplates',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/cctemplates`						
			};

			const integrationsItem = {
				name: 'ccIntegrations',
				translationKey: 'Integrations',
				id: 'ccintegrations',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/ccintegrations`						
			};

			const numbersItem = {
				name: 'ccNumbers',
				translationKey: 'Numbers',
				id: 'ccnumberss',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/ccnumbers`						
			};

			const settingsItem = {
				name: 'ccSettings',
				translationKey: 'Settings',
				id: 'ccsettings',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/ccsettings`						
			};

			const helpItem = {
				name: 'ccHelp',
				translationKey: 'Help',
				id: 'cchelpworkgroups',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: this.translateService.instant('integrations.workgroups.help-url'),
				openInNewWindow: true,				
			};

			const callCenterItem = {
				name: 'callCenter',
				translationKey: 'ContactCenter',
				id: 'callcenter',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: '/norouting',
				iconActive: '../../../../assets/images/contactCenter_active.svg',
				iconInactive: '../../../../assets/images/contactCenter_inactive.svg',
				children: [
					dashboardItem,
					skillsGroupItem,
					routingItem,
					codesItem,
					templatesItem,
					integrationsItem,
					numbersItem,
					settingsItem,
					helpItem
				]						
			};
			console.log("return left nav menus", [callCenterItem])
			return Promise.resolve( [callCenterItem] );
		});
	}

	isIntegrationStateChangeable(account: Account): Promise<boolean> {
		this.initServices();
		
		let isDenied = this.rbacAuthGuardService.isPermissionDeniedForUser(CategoryId.INTEGRATIONS, ActionType.DELETE);
		return Promise.resolve(!isDenied);
	}
}
